import { useState } from "react";
import { ApiError, api, type Uuid } from "@shared/api";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Card, Row } from "@shared/ui/settings-card";

const TAG = /^[0-9A-Za-zА-Яа-яЁё]{2,4}$/;

/** Метка тега рядом с именем — как в Discord. */
export function TagChip({ tag }: { tag: string | null | undefined }) {
  return tag ? <span className="name-tag">{tag}</span> : null;
}

/**
 * Тег сервера — как в Discord: 2–4 буквы или цифры. Задаёт его тот, кто управляет
 * сервером; носить — любой участник, один тег на человека, а выбирают его у себя
 * в профиле (`TagPicker`).
 */
export function TagTab({
  serverId,
  tag,
  canManage,
  onChanged,
}: {
  serverId: Uuid;
  tag: string | null;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState(tag ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const trimmed = draft.trim();
  const valid = trimmed === "" || TAG.test(trimmed);

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    setNote(null);
    try {
      await action();
      setNote({ ok: true, text: done });
    } catch (error) {
      setNote({ ok: false, text: error instanceof ApiError ? error.message : "Не получилось" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="automod">
      <Card title="Тег сервера">
        <Row
          title="Тег"
          note="2–4 буквы или цифры. Участники выбирают его у себя в профиле, и он стоит у их имени на всех серверах."
        >
          <div className="line" style={{ gap: 8, alignItems: "center" }}>
            <Input
              className="w-[110px]"
              value={draft}
              maxLength={4}
              disabled={!canManage}
              placeholder="нет"
              aria-label="Тег сервера"
              onChange={(event) => setDraft(event.target.value)}
            />
            {canManage && (
              <Button
                type="button"
                size="sm"
                disabled={busy || !valid || trimmed === (tag ?? "")}
                onClick={() =>
                  void run(async () => {
                    await api.servers.update(serverId, { tag: trimmed || null });
                    onChanged();
                  }, trimmed ? "Тег сохранён" : "Тег снят")
                }
              >
                Сохранить
              </Button>
            )}
          </div>
        </Row>
      </Card>
      {note && <div className={`small ${note.ok ? "muted" : "danger-text"}`}>{note.text}</div>}
    </div>
  );
}
